import { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import type { PlaybookGroup } from "../../services/lensService";
import { UNGROUPED } from "./provisionGroups";

interface GroupNameDialogProps {
    open: boolean;
    title: string;
    confirmLabel: string;
    initialName?: string;
    // For the duplicate-name check; except is the group being renamed.
    groups: PlaybookGroup[];
    except?: number;
    onConfirm: (name: string) => void;
    onClose: () => void;
}

const GroupNameDialog = ({ open, title, confirmLabel, initialName = "", groups, except, onConfirm, onClose }: GroupNameDialogProps) => {
    const [name, setName] = useState(initialName);

    useEffect(() => {
        if (open) setName(initialName);
    }, [open, initialName]);

    const trimmed = name.trim();
    const lower = trimmed.toLowerCase();
    const problem =
        trimmed.length > 200
            ? "A group name can be at most 200 characters."
            : lower === UNGROUPED.toLowerCase()
              ? `"${UNGROUPED}" is the heading for provisions with no group.`
              : groups.some((g) => g.id !== except && g.name.toLowerCase() === lower)
                ? "This playbook already has a group with that name."
                : undefined;
    const canConfirm = !!trimmed && !problem && trimmed !== initialName;

    const confirm = () => {
        if (!canConfirm) return;
        onClose();
        onConfirm(trimmed);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Group name"
                    placeholder="e.g. Holiday"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && confirm()}
                    error={!!problem}
                    helperText={problem}
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!canConfirm} onClick={confirm}>
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GroupNameDialog;
